import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { ProvinceStateViewStore } from "../../store";
import { ProvinceStateView } from "../view/ProvinceStateView";
import { ProvinceStateUpdateStore } from "../../store";
import { ProvinceStateUpdate } from "../update/ProvinceStateUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type ProvinceStateModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const ProvinceStateModalContent: React.FC<ProvinceStateModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new ProvinceStateUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new ProvinceStateViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <ProvinceStateView
            provinceStateViewStore={storeInfo.store as ProvinceStateViewStore}
          />
        )}
        {storeInfo.type === "update" && (
          <ProvinceStateUpdate
            provinceStateUpdateStore={
              storeInfo.store as ProvinceStateUpdateStore
            }
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
